import { createEffect, createRoot } from 'solid-js';

/**
 * Waits for a SolidJS reactive accessor to satisfy a predicate.
 * Resolves immediately if the condition is already met,
 * otherwise watches the signal until it is.
 *
 * @param timeoutMs - If provided, rejects after this many milliseconds.
 */
export function waitForReactive<T>(
  accessor: () => T,
  predicate: (value: T) => boolean,
  timeoutMs?: number
): Promise<T> {
  const current = accessor();
  if (predicate(current)) {
    return Promise.resolve(current);
  }

  return new Promise<T>((resolve, reject) => {
    createRoot((dispose) => {
      let timer: ReturnType<typeof setTimeout> | undefined;

      if (timeoutMs !== undefined) {
        timer = setTimeout(() => {
          dispose();
          reject(new Error(`waitForReactive timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }

      createEffect(() => {
        const value = accessor();
        if (predicate(value)) {
          if (timer !== undefined) clearTimeout(timer);
          dispose();
          resolve(value);
        }
      });
    });
  });
}
